import { useRef, useState } from 'react';
import { Icon } from '@/lib/icons';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useApp } from '@/hooks/use-app-context';
import { safePlay } from '@/lib/audio-playback';
import { ModelPerformanceHeader } from './ModelPerformanceHeader';
import { StarRating } from './StarRating';
import { Field } from './Field';

interface TTSScreenProps {
  language: string;
  model: string;
  currentTtsIndex: number;
  totalPhrases: number;
  currentPage: number;
  currentTtsText: string;
  generatedAudioUrl: string;
  referenceAudioUrl: string;
  isGeneratingTts: boolean;
  qualityRating: number | null;
  naturalness: number | null;
  pronunciation: number | null;
  ttsComments: string;
  totalTtsAssessments: number;
  onSetQualityRating: (v: number) => void;
  onSetNaturalness: (v: number) => void;
  onSetPronunciation: (v: number) => void;
  onSetTtsComments: (v: string) => void;
  onGenerateTts: () => void;
  onSubmit: () => void;
  onPrev: () => void;
  onBack: () => void;
}

export function TTSScreen({
  language,
  model,
  currentTtsIndex,
  totalPhrases,
  currentPage,
  currentTtsText,
  generatedAudioUrl,
  referenceAudioUrl,
  isGeneratingTts,
  qualityRating,
  naturalness,
  pronunciation,
  ttsComments,
  totalTtsAssessments,
  onSetQualityRating,
  onSetNaturalness,
  onSetPronunciation,
  onSetTtsComments,
  onGenerateTts,
  onSubmit,
  onPrev,
  onBack,
}: TTSScreenProps) {
  const { t } = useApp();
  const tModel = t.modelPerformancePage;
  const groundTruthRef = useRef<HTMLAudioElement>(null);
  const generatedRef = useRef<HTMLAudioElement>(null);
  const [isPlayingGroundTruth, setIsPlayingGroundTruth] = useState(false);
  const [isPlayingGenerated, setIsPlayingGenerated] = useState(false);

  const progress = totalPhrases > 0 ? ((currentTtsIndex + 1) / totalPhrases) * 100 : 0;
  const averageScore = qualityRating && naturalness && pronunciation
    ? ((qualityRating + naturalness + pronunciation) / 3).toFixed(1)
    : '—';

  const toggleGroundTruthPlay = () => {
    if (!groundTruthRef.current) return;
    if (isPlayingGroundTruth) {
      groundTruthRef.current.pause();
      setIsPlayingGroundTruth(false);
    } else {
      if (generatedRef.current) { generatedRef.current.pause(); setIsPlayingGenerated(false); }
      setIsPlayingGroundTruth(true);
      safePlay(groundTruthRef.current, { onError: () => setIsPlayingGroundTruth(false) });
    }
  };

  const toggleGeneratedPlay = () => {
    if (!generatedRef.current) return;
    if (isPlayingGenerated) {
      generatedRef.current.pause();
      setIsPlayingGenerated(false);
    } else {
      if (groundTruthRef.current) { groundTruthRef.current.pause(); setIsPlayingGroundTruth(false); }
      setIsPlayingGenerated(true);
      safePlay(generatedRef.current, { onError: () => setIsPlayingGenerated(false) });
    }
  };

  return (
    <div>
      <div className="space-y-6 sm:space-y-8">
        <ModelPerformanceHeader
          model={model} language={language}
          currentIndex={currentTtsIndex} total={totalPhrases}
          currentPage={currentPage} progressPercent={progress}
          onBack={onBack}
        />

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Source card — phrase to synthesize + audio comparison */}
          <section className="h-fit space-y-4">
            <p className="flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-primary/70">
              {tModel.phraseToSynthesizeTitle}
            </p>

            <div className="rounded-2xl border border-border/40 bg-background/60 p-4 sm:p-6 space-y-4">
              <div className="rounded-xl border border-dashed border-border/40 bg-muted/20 p-5 text-center min-h-[6rem] flex items-center justify-center">
                <span className="text-base font-semibold leading-relaxed tracking-[-0.02em] text-foreground sm:text-lg">
                  {'\u201c'}{currentTtsText || '-'}{'\u201d'}
                </span>
              </div>

              <Button
                onClick={onGenerateTts}
                disabled={isGeneratingTts || !currentTtsText}
                className="h-10 rounded-full border border-primary/20 bg-primary/8 px-5 text-xs font-semibold text-primary hover:bg-primary/15 disabled:border-border/30 disabled:bg-background/40 disabled:text-muted-foreground"
              >
                {isGeneratingTts ? (
                  <><Icon name="loader2" className="mr-2 h-4 w-4 animate-spin" />{tModel.generatingAudio}</>
                ) : (
                  <><Icon name="volume2" className="mr-2 h-4 w-4" />{tModel.generateAudio}</>
                )}
              </Button>
            </div>

            {generatedAudioUrl && (
              <div className="space-y-3">
                {referenceAudioUrl && (
                  <div className="rounded-2xl border border-border/40 bg-background/60 p-4 sm:p-5 space-y-3">
                    <p className="flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-primary/70">
                      {String(tModel.referenceAudioLabel ?? '')}
                    </p>
                    <audio ref={groundTruthRef} src={referenceAudioUrl} onEnded={() => setIsPlayingGroundTruth(false)} className="hidden" />
                    <button
                      type="button"
                      onClick={toggleGroundTruthPlay}
                      className={`group/rec relative flex h-11 w-full items-center justify-center gap-2.5 overflow-hidden rounded-xl sm:mx-auto sm:w-auto sm:px-8 border text-xs font-semibold transition-all duration-200 ${
                        isPlayingGroundTruth
                          ? 'border-danger/25 bg-danger/[0.08] text-danger hover:bg-danger/[0.12]'
                          : 'border-primary/20 bg-primary/8 text-primary hover:bg-primary/15'
                      }`}
                    >
                      <span className="relative flex h-6 w-6 items-center justify-center rounded-full border border-current/15 bg-current/8">
                        <Icon name={isPlayingGroundTruth ? 'pause' : 'play'} className="h-3 w-3" />
                      </span>
                      <span>{isPlayingGroundTruth ? t.pauseAudio : t.playAudio}</span>
                    </button>
                  </div>
                )}

                <div className="rounded-2xl border border-border/40 bg-background/60 p-4 sm:p-5 space-y-3">
                  <p className="flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-primary/70">
                    {tModel.generatedModelAudioTitle}
                  </p>
                  <audio ref={generatedRef} src={generatedAudioUrl} onEnded={() => setIsPlayingGenerated(false)} className="hidden" />
                  <button
                    type="button"
                    onClick={toggleGeneratedPlay}
                    className={`group/rec relative flex h-11 w-full items-center justify-center gap-2.5 overflow-hidden rounded-xl sm:mx-auto sm:w-auto sm:px-8 border text-xs font-semibold transition-all duration-200 ${
                      isPlayingGenerated
                        ? 'border-danger/25 bg-danger/[0.08] text-danger hover:bg-danger/[0.12]'
                        : 'border-primary/20 bg-primary/8 text-primary hover:bg-primary/15'
                    }`}
                  >
                    <span className="relative flex h-6 w-6 items-center justify-center rounded-full border border-current/15 bg-current/8">
                      <Icon name={isPlayingGenerated ? 'pause' : 'play'} className="h-3 w-3" />
                    </span>
                    <span>{isPlayingGenerated ? t.pauseAudio : t.playAudio}</span>
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Action card — rating + comments + nav */}
          <section className="space-y-4">
            <p className="flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.28em] text-primary/70">
              {tModel.humanEvaluationTitle}
            </p>

            <div className="rounded-2xl border border-border/40 bg-background/60 p-4 sm:p-6 space-y-5 sm:space-y-6">
              <StarRating value={qualityRating} onChange={onSetQualityRating} label={tModel.qualityRatingLabel} />
              <StarRating value={naturalness} onChange={onSetNaturalness} label={tModel.naturalnessLabel} />
              <StarRating value={pronunciation} onChange={onSetPronunciation} label={tModel.pronunciationLabel} />

              <Field label={tModel.commentsLabel}>
                <Textarea
                  value={ttsComments}
                  onChange={(e) => onSetTtsComments(e.target.value)}
                  placeholder={tModel.commentsPlaceholderTts}
                  rows={3}
                  className="min-h-[80px] resize-none rounded-xl border-border/40 bg-background/50 px-4 py-3 text-sm text-foreground shadow-none placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-primary/30"
                />
              </Field>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-[0.62rem] text-muted-foreground/60">
                  {tModel.statsAssessmentsLabel}: {totalTtsAssessments} · {tModel.statsCurrentScoreLabel}: {averageScore}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={onPrev}
                    disabled={currentTtsIndex <= 0}
                    variant="outline"
                    size="sm"
                    className="rounded-full border-border/40 bg-background/50 px-4 text-[0.68rem] font-semibold disabled:opacity-40"
                  >
                    {String(t.previous ?? '')}
                  </Button>
                  <Button
                    type="button"
                    onClick={onSubmit}
                    disabled={qualityRating === null || naturalness === null || pronunciation === null}
                    size="sm"
                    className="rounded-full bg-primary px-6 text-[0.68rem] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                  >
                    <Icon name="checkCircle" className="mr-1.5 h-3.5 w-3.5" />
                    {String(t.submit ?? '')}
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
